// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { alphabetNavigationStyles } from "@conklin/Overrides/Widgets/Brand/AlphabetNavigation";
import IconSearch from "@conklin/XDL/Icons/IconSearch";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, mergeWithCss, searchBoxFieldClickableStyles, xScreen } from "@conklin/XDL/Utilities";
import { FormFieldClickable, FormFieldStyle } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

alphabetNavigationStyles.container = {
    css: css`
        padding: 12px;
        background: ${color("common.ui2")};
        border: 1px solid ${color("common.border")};
        border-radius: 4px;
        ${xScreen("lte", "md")} {
            padding: 16px;
        }
    `,
};

alphabetNavigationStyles.brandsLettersWrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;

        ${xScreen("lte", "md")} {
            align-items: flex-end;
            row-gap: 12px;
        }

        ${xScreen("lte", "sm")} {
            flex-direction: column;
            align-items: center;

            & > div {
                width: 100%;
            }
        }
    `,
};

alphabetNavigationStyles.selectWrapper = {
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            display: flex;
            flex-direction: column;
            flex: 1;
        }
    `,
};

alphabetNavigationStyles.select = {
    labelProps: {
        size: 16,
        css: css`
            margin-bottom: 0;
            line-height: 17px;
        `,
    },
};

alphabetNavigationStyles.alphabetSection = {
    css: css`
        display: flex;
        flex: 1;
        flex-direction: column;
        ${xScreen("lte", "md")} {
            display: none;
        }
    `,
};

alphabetNavigationStyles.alphabetContainer = {
    css: css`
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    `,
};

alphabetNavigationStyles.filterSection = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        ${FormFieldStyle} {
            width: 100%;
        }
    `,
};

alphabetNavigationStyles.filterInput = {
    iconProps: {
        src: IconSearch,
    },
    cssOverrides: {
        formInputWrapper: css`
            ${FormFieldClickable} {
                background-color: transparent;

                &:disabled ${IconWrapper} {
                    color: ${color("common.disabled")};
                }
            }
            input:placeholder-shown ~ .clear {
                display: block !important;
            }
            &&&& input {
                padding-right: 50px;
            }
        `,
    },
};

alphabetNavigationStyles.navigationItem = {
    css: css`
        flex: 0 0 auto;
        display: flex;
        margin: 0;
    `,
};

alphabetNavigationStyles.titleText = {
    variant: "h5",
    forwardAs: "h2",
    css: css`
        margin-bottom: 8px;
    `,
};

alphabetNavigationStyles.navigationLink = {
    ...mergeWithCss(alphabetNavigationStyles.navigationLink, {
        typographyProps: {
            transform: "capitalize",
            lineHeight: "19px",
            size: 16,
        },
        css: css`
            ${TypographyStyle} {
                color: ${color("common.ui10")};
                text-decoration: ${({ selectedLetter, disabled }: any) => (selectedLetter ? "underline" : "none")};
            }
        `,
    }),
};

alphabetNavigationStyles.navigationTextEmpty = {
    transform: "capitalize",
    lineHeight: "23px",
    size: 15,
    weight: fontConfig.weight.bold,
    color: "common.ui3",
    css: css`
        display: flex;
        align-items: center;
        cursor: not-allowed;
    `,
};
